(function () {
  const supportedTypes = ['image/png', 'image/jpeg', 'image/webp'];
  const supportedExtensions = ['.png', '.jpg', '.jpeg', '.webp'];

  function bytesToMb(bytes) {
    return Math.round((Number(bytes || 0) / 1024 / 1024) * 10) / 10;
  }

  function fileExtension(fileName = '') {
    const lowerName = String(fileName || '').toLowerCase();
    return supportedExtensions.find((extension) => lowerName.endsWith(extension)) || '';
  }

  function fileTypeLabel(file) {
    const extension = fileExtension(file?.name);
    if (extension) return extension.replace('.', '').toUpperCase().replace('JPG', 'JPEG');
    if (file?.type) return file.type;
    return 'Unknown';
  }

  function isSupportedFile(file) {
    return supportedTypes.includes(file?.type) && Boolean(fileExtension(file?.name));
  }

  function loadImage(file) {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error('No artwork file selected.'));
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Unable to read image dimensions.'));
      };

      image.src = objectUrl;
    });
  }

  function detectTransparency(image, maxSampleSize = 420) {
    const width = image.naturalWidth || image.width || 1;
    const height = image.naturalHeight || image.height || 1;
    const scale = Math.min(maxSampleSize / Math.max(width, height), 1);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });

    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    if (!context) return false;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let index = 3; index < pixels.length; index += 4) {
      if (pixels[index] < 250) return true;
    }

    return false;
  }

  function row(label, status, text) {
    return { label, status, text };
  }

  async function analyzeFile(file, options = {}) {
    const maxFileSizeMb = Number(options.maxFileSizeMb || 25);
    const minRecommendedLongestSide = Number(options.minRecommendedLongestSide || 1000);
    const mediumLongestSide = Math.max(500, Math.round(minRecommendedLongestSide / 2));
    const supported = isSupportedFile(file);
    const sizeMb = bytesToMb(file?.size);
    const result = {
      fileName: file?.name || '',
      fileType: fileTypeLabel(file),
      fileSizeMb: sizeMb,
      naturalWidth: 0,
      naturalHeight: 0,
      transparent: null,
      rows: [],
      readiness: 'issue',
      readinessText: 'Low quality warning - better artwork recommended.',
    };

    result.rows.push(row(
      'File type',
      supported ? 'pass' : 'issue',
      supported ? `${result.fileType} is supported.` : 'Unsupported file type. Upload PNG, JPG, JPEG, or WEBP artwork.'
    ));

    const fileSizeStatus = sizeMb <= maxFileSizeMb ? 'pass' : (options.fileSizeBlocks ? 'issue' : 'warning');
    result.rows.push(row(
      'File size',
      fileSizeStatus,
      `${sizeMb || 0} MB. Recommended max is ${maxFileSizeMb} MB.`
    ));

    let image = null;
    if (supported) {
      try {
        image = await loadImage(file);
        result.naturalWidth = image.naturalWidth || image.width || 0;
        result.naturalHeight = image.naturalHeight || image.height || 0;
      } catch (error) {
        result.rows.push(row('Image resolution', 'issue', 'Image dimensions could not be read.'));
      }
    } else {
      result.rows.push(row('Image resolution', 'issue', 'Image dimensions could not be checked for this file type.'));
    }

    if (image) {
      const longestSide = Math.max(result.naturalWidth, result.naturalHeight);
      let resolutionStatus = 'issue';
      let resolutionText = `${result.naturalWidth} x ${result.naturalHeight} px. This file may look pixelated if printed large.`;

      if (longestSide >= minRecommendedLongestSide) {
        resolutionStatus = 'pass';
        resolutionText = `${result.naturalWidth} x ${result.naturalHeight} px. Good resolution for most print uses.`;
      } else if (longestSide >= mediumLongestSide) {
        resolutionStatus = 'warning';
        resolutionText = `${result.naturalWidth} x ${result.naturalHeight} px. Usable, but higher resolution is recommended for larger prints.`;
      }

      result.rows.push(row('Image resolution', resolutionStatus, resolutionText));
    }

    const type = String(file?.type || '').toLowerCase();
    if (image && (type === 'image/png' || type === 'image/webp')) {
      result.transparent = detectTransparency(image);
      result.rows.push(row(
        'Transparent background',
        result.transparent ? 'pass' : 'warning',
        result.transparent
          ? 'Transparent background detected. This is ideal for DTF printing.'
          : 'No transparency detected. Your design may print with a background.'
      ));
    } else if (type === 'image/jpeg') {
      result.transparent = false;
      result.rows.push(row(
        'Transparent background',
        'warning',
        'JPG files do not support transparency. If you need no background, upload a transparent PNG.'
      ));
    } else if (!supported) {
      result.rows.push(row('Transparent background', 'issue', 'Transparency could not be checked for this file type.'));
    }

    if (type === 'image/png') {
      result.rows.push(row(
        'Recommended format',
        result.transparent ? 'pass' : 'warning',
        result.transparent
          ? 'PNG with transparency is the best format for DTF printing.'
          : 'PNG uploaded, but no transparent background was detected.'
      ));
    } else if (type === 'image/jpeg') {
      result.rows.push(row('Recommended format', 'warning', 'JPG uploaded. Transparent PNG is recommended for best results.'));
    } else if (type === 'image/webp') {
      result.rows.push(row('Recommended format', 'warning', 'WEBP uploaded. PNG is recommended for production artwork if available.'));
    } else {
      result.rows.push(row('Recommended format', 'issue', 'Upload a transparent PNG when possible.'));
    }

    const hasIssue = result.rows.some((item) => item.status === 'issue');
    const hasWarning = result.rows.some((item) => item.status === 'warning');
    if (!hasIssue && !hasWarning) {
      result.readiness = 'pass';
      result.readinessText = 'Looks good for print. We will still review artwork before production.';
    } else if (!hasIssue) {
      result.readiness = 'warning';
      result.readinessText = 'Usable, but review recommended. We will contact you if anything needs adjustment.';
    }

    result.rows.push(row('Print readiness', result.readiness, result.readinessText));

    if (options.debug) {
      console.debug('[InkNfiber artwork quality]', {
        fileName: result.fileName,
        fileType: result.fileType,
        fileSizeMb: result.fileSizeMb,
        naturalWidth: result.naturalWidth,
        naturalHeight: result.naturalHeight,
        transparencyDetected: result.transparent,
        readiness: result.readiness,
      });
    }

    return result;
  }

  function statusLabel(status) {
    if (status === 'pass') return 'Pass';
    if (status === 'issue') return 'Issue';
    return 'Warning';
  }

  function render(container, result) {
    if (!container || !result) return;
    container.hidden = false;
    container.innerHTML = '';

    const card = document.createElement('div');
    card.className = 'inknfiber-artwork-check';

    const header = document.createElement('div');
    header.className = 'inknfiber-artwork-check__header';
    header.innerHTML = '<h3>Artwork Quality Check</h3><p>We&apos;ll review your file before production, but this helps catch common artwork issues.</p>';

    const list = document.createElement('div');
    list.className = 'inknfiber-artwork-check__list';

    result.rows.forEach((item) => {
      const rowNode = document.createElement('div');
      rowNode.className = 'inknfiber-artwork-check__row';
      rowNode.dataset.status = item.status;

      const copy = document.createElement('div');
      const label = document.createElement('strong');
      const text = document.createElement('span');
      label.textContent = item.label;
      text.textContent = item.text;
      copy.append(label, text);

      const badge = document.createElement('span');
      badge.className = `inknfiber-artwork-check__badge inknfiber-artwork-check__badge--${item.status}`;
      badge.textContent = statusLabel(item.status);

      rowNode.append(copy, badge);
      list.append(rowNode);
    });

    const note = document.createElement('p');
    note.className = 'inknfiber-artwork-check__note';
    note.textContent = 'We review artwork before production and contact you if anything needs adjustment.';

    card.append(header, list, note);
    container.append(card);
  }

  function reset(container, neutral = false) {
    if (!container) return;
    container.innerHTML = '';
    container.hidden = !neutral;
    if (neutral) {
      const card = document.createElement('div');
      card.className = 'inknfiber-artwork-check inknfiber-artwork-check--empty';
      card.innerHTML = '<div class="inknfiber-artwork-check__header"><h3>Artwork Quality Check</h3><p>Upload artwork to run a quality check.</p></div>';
      container.append(card);
    }
  }

  function resultPasses(result, options = {}) {
    if (!result || !Array.isArray(result.rows)) return false;
    const requireTransparent = options.requireTransparent !== false;
    const hasBlockingStatus = result.rows.some((item) => item.status === 'warning' || item.status === 'issue');

    if (hasBlockingStatus) return false;
    if (result.readiness !== 'pass') return false;
    if (requireTransparent && result.transparent !== true) return false;

    return true;
  }

  function summarizeResults(results = [], options = {}) {
    const validResults = results.filter(Boolean);
    const strictPass = validResults.length > 0 && validResults.every((result) => resultPasses(result, options));
    const requirePass = options.requirePass !== false;

    return {
      hasResults: validResults.length > 0,
      strictPass,
      continueAllowed: validResults.length > 0 && (strictPass || !requirePass),
      failedResults: validResults.filter((result) => !resultPasses(result, options)),
    };
  }

  function buildRows(result) {
    const list = document.createElement('div');
    list.className = 'inknfiber-artwork-check__list';

    result.rows.forEach((item) => {
      const rowNode = document.createElement('div');
      rowNode.className = 'inknfiber-artwork-check__row';
      rowNode.dataset.status = item.status;

      const copy = document.createElement('div');
      const label = document.createElement('strong');
      const text = document.createElement('span');
      label.textContent = item.label;
      text.textContent = item.text;
      copy.append(label, text);

      const badge = document.createElement('span');
      badge.className = `inknfiber-artwork-check__badge inknfiber-artwork-check__badge--${item.status}`;
      badge.textContent = statusLabel(item.status);

      rowNode.append(copy, badge);
      list.append(rowNode);
    });

    return list;
  }

  function buildModalResult(result, options = {}) {
    const file = document.createElement('section');
    file.className = 'inknfiber-artwork-modal__file';
    file.dataset.passed = resultPasses(result, options) ? 'true' : 'false';

    const header = document.createElement('div');
    header.className = 'inknfiber-artwork-modal__file-header';

    const title = document.createElement('h3');
    title.textContent = result.fileName || 'Uploaded artwork';

    const badge = document.createElement('span');
    badge.className = resultPasses(result, options)
      ? 'inknfiber-artwork-check__badge inknfiber-artwork-check__badge--pass'
      : 'inknfiber-artwork-check__badge inknfiber-artwork-check__badge--warning';
    badge.textContent = resultPasses(result, options) ? 'Pass' : 'Review';

    header.append(title, badge);
    file.append(header, buildRows(result));

    return file;
  }

  function openModal(results = [], options = {}) {
    const validResults = results.filter(Boolean);
    const summary = summarizeResults(validResults, options);

    return new Promise((resolve) => {
      const previousFocus = document.activeElement;
      const overlay = document.createElement('div');
      overlay.className = 'inknfiber-artwork-modal';
      overlay.setAttribute('role', 'presentation');

      const dialog = document.createElement('div');
      dialog.className = 'inknfiber-artwork-modal__dialog';
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-labelledby', 'inknfiber-artwork-modal-title');
      dialog.setAttribute('aria-describedby', 'inknfiber-artwork-modal-copy');

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'inknfiber-artwork-modal__close';
      close.setAttribute('aria-label', 'Close artwork quality check');
      close.textContent = 'x';

      const eyebrow = document.createElement('p');
      eyebrow.className = 'inknfiber-artwork-modal__eyebrow';
      eyebrow.textContent = 'Pre-cart review';

      const title = document.createElement('h2');
      title.id = 'inknfiber-artwork-modal-title';
      title.textContent = 'Artwork Quality Check';

      const copy = document.createElement('p');
      copy.id = 'inknfiber-artwork-modal-copy';
      copy.textContent = 'Review your artwork before adding this order to cart.';

      const status = document.createElement('div');
      status.className = summary.strictPass
        ? 'inknfiber-artwork-modal__status is-pass'
        : 'inknfiber-artwork-modal__status is-warning';
      status.textContent = summary.strictPass
        ? 'Your artwork looks ready for print.'
        : 'Your artwork needs review before adding to cart.';

      const body = document.createElement('div');
      body.className = 'inknfiber-artwork-modal__body';

      if (validResults.length) {
        validResults.forEach((result) => body.append(buildModalResult(result, options)));
      } else {
        const empty = document.createElement('p');
        empty.className = 'inknfiber-artwork-modal__empty';
        empty.textContent = 'Please upload artwork before adding to cart.';
        body.append(empty);
      }

      const note = document.createElement('p');
      note.className = 'inknfiber-artwork-check__note';
      note.textContent = 'We review all artwork before production and will contact you if anything needs adjustment.';

      const actions = document.createElement('div');
      actions.className = 'inknfiber-artwork-modal__actions';

      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'inknfiber-artwork-modal__button inknfiber-artwork-modal__button--secondary';
      back.textContent = 'Go Back / Review Artwork';

      if (summary.continueAllowed) {
        const continueButton = document.createElement('button');
        continueButton.type = 'button';
        continueButton.className = 'inknfiber-artwork-modal__button inknfiber-artwork-modal__button--primary';
        continueButton.textContent = 'Continue to Cart';
        actions.append(continueButton, back);
        continueButton.addEventListener('click', () => finish(true));
      } else {
        actions.append(back);
      }

      const header = document.createElement('div');
      header.className = 'inknfiber-artwork-modal__header';
      header.append(eyebrow, title, copy, status);

      dialog.append(close, header, body, note, actions);
      overlay.append(dialog);

      function finish(allowed) {
        document.removeEventListener('keydown', onKeydown);
        overlay.remove();
        if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
        resolve(Boolean(allowed));
      }

      function onKeydown(event) {
        if (event.key === 'Escape') finish(false);
      }

      close.addEventListener('click', () => finish(false));
      back.addEventListener('click', () => finish(false));
      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) finish(false);
      });
      document.addEventListener('keydown', onKeydown);
      document.body.append(overlay);

      const firstAction = summary.continueAllowed
        ? actions.querySelector('.inknfiber-artwork-modal__button--primary')
        : back;
      firstAction?.focus();

      if (options.debug) {
        console.debug('[InkNfiber artwork quality modal]', {
          filesChecked: validResults.map((result) => result.fileName),
          strictPass: summary.strictPass,
          continueAllowed: summary.continueAllowed,
          failedFiles: summary.failedResults.map((result) => result.fileName),
        });
      }
    });
  }

  window.InkNfiberArtworkQualityChecker = {
    analyzeFile,
    openModal,
    render,
    resultPasses,
    reset,
    summarizeResults,
  };
}());
